import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrendUp, Users, FileText, CurrencyInr, Flame, Snowflake, Sun } from "@phosphor-icons/react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import Layout from "../components/Layout";
import api from "../lib/api";

const COLORS = {
  new: "#3A86FF", cold: "#8D99AE", warm: "#F4A261", hot: "#D90429",
  proposal_sent: "#7C3AED", won: "#2A9D8F", lost: "#0A0A0A",
};

const formatINR = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function Stat({ label, value, icon: Icon, accent = false, testId }) {
  return (
    <div data-testid={testId} className={`border border-black/10 p-6 ${accent ? "bg-[#D90429] text-white" : "bg-white"} transition-all duration-200 hover:-translate-y-0.5`}>
      <div className="flex items-center justify-between">
        <div className={`text-xs tracking-[0.2em] uppercase font-bold ${accent ? "text-white/80" : "text-[#D90429]"}`}>{label}</div>
        <Icon size={20} weight="bold" className={accent ? "text-white/90" : "text-[#D90429]"} />
      </div>
      <div className="font-display text-4xl font-black tracking-tighter mt-3">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/leads").then(r => setLeads(r.data.slice(0, 8))).catch(() => {});
  }, []);

  const statusData = stats ? Object.entries(stats.by_status).map(([name, value]) => ({ name, value })) : [];
  const sourceData = stats ? Object.entries(stats.by_source).map(([name, value]) => ({ name, value })) : [];

  return (
    <Layout title="Dashboard" subtitle="Real-time view of your pipeline and activity">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-testid="dashboard-stats">
        <Stat testId="stat-pipeline" label="Pipeline Value" value={formatINR(stats?.pipeline_value || 0)} icon={CurrencyInr} accent />
        <Stat testId="stat-leads" label="Total Leads" value={stats?.total_leads ?? 0} icon={Users} />
        <Stat testId="stat-proposals" label="Proposals" value={stats?.total_proposals ?? 0} icon={FileText} />
        <Stat testId="stat-won" label="Won Value" value={formatINR(stats?.won_value || 0)} icon={TrendUp} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <div className="lg:col-span-7 border border-black/10 p-6 bg-white">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline">Pipeline</div>
              <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Leads by status</h3>
            </div>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={statusData}>
                <CartesianGrid stroke="#EBECEF" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="name" stroke="#717680" fontSize={12} />
                <YAxis stroke="#717680" fontSize={12} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#F5F6F8" }} contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0 }} />
                <Bar dataKey="value">
                  {statusData.map((e) => <Cell key={`status-${e.name}`} fill={COLORS[e.name] || "#D90429"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-5 border border-black/10 p-6 bg-white">
          <div className="overline">Sources</div>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-1 mb-4">Where leads come from</h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={sourceData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {sourceData.map((e, i) => <Cell key={`source-${e.name}`} fill={["#D90429","#0A0A0A","#F4A261","#3A86FF","#2A9D8F","#8D99AE","#7C3AED"][i % 7]} />)}
                </Pie>
                <Tooltip contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {sourceData.map((s, i) => (
              <div key={s.name} className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2" style={{ background: ["#D90429","#0A0A0A","#F4A261","#3A86FF","#2A9D8F","#8D99AE","#7C3AED"][i % 7] }} />
                <span className="text-[#0A0A0A]">{s.name}</span>
                <span className="text-[#717680]">({s.value})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-black/10 bg-white">
        <div className="flex items-center justify-between p-6 border-b border-black/10">
          <div>
            <div className="overline">Activity</div>
            <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Recent leads</h3>
          </div>
          <button onClick={() => navigate("/leads")} data-testid="view-all-leads" className="text-sm font-semibold text-[#D90429] hover:underline">View all →</button>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="recent-leads-table">
          <thead className="bg-[#F5F6F8] text-left">
            <tr>
              <th className="px-6 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">Lead</th>
              <th className="px-6 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">Company</th>
              <th className="px-6 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">Source</th>
              <th className="px-6 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">Status</th>
              <th className="px-6 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680] text-right">Value</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-[#717680]">No leads yet. Add or import some on the Leads page.</td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-black/5 hover:bg-[#FAFBFC]">
                <td className="px-6 py-3 font-semibold">{l.name}</td>
                <td className="px-6 py-3 text-[#4A4D57]">{l.company || "—"}</td>
                <td className="px-6 py-3 text-[#4A4D57]">{l.source}</td>
                <td className="px-6 py-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1" style={{ background: (COLORS[l.status] || "#0A0A0A") + "20", color: COLORS[l.status] || "#0A0A0A" }}>
                    {l.status === "hot" && <Flame size={12} weight="fill" />}
                    {l.status === "warm" && <Sun size={12} weight="fill" />}
                    {l.status === "cold" && <Snowflake size={12} weight="fill" />}
                    {l.status}
                  </span>
                </td>
                <td className="px-6 py-3 text-right font-mono-shelv">{formatINR(l.value || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </Layout>
  );
}
